import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";

import { merge } from "lodash";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import CheckboxFormField from "components/checkbox-form-field/index";
import ErrorSnackbarContent from "components/error-snackbar-content/index";
import InputFormField from "components/input-form-field/index";
import ModalForm from "components/modal-form/index";
import SelectFormField from "components/select-form-field/index";
import { EXTENDED_SUB_REFERRAL_SOURCE_FEATURE } from "constants/feature-flags";
import { REFERRAL_SOURCE_OBJECT_TYPES } from "constants/referral_sources";
import { ReferralSourceFormValues } from "features/settings/referral-sources/referral-source-form.component";
import { useAddReferralSourceMutation } from "services/referral-sources";

interface ReferralSourceShortFormProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (id: number) => void;
}

const EMPTY_DEFAULT_VALUES: ReferralSourceFormValues = {
  name: "",
  is_active: true,
  referred_type: ""
};

export default function ReferralSourceShortForm({
  isOpen,
  onClose,
  onAdd
}: ReferralSourceShortFormProps) {
  const dispatch = useAppDispatch();
  const [createReferralSource] = useAddReferralSourceMutation();
  const onSuccess = () => {
    dispatch(setCustomMessage("Referral Source added"));
    dispatch(setOpen(true));
    onClose();
  };

  const onReferralSourceSave = useCallback(
    (data: ReferralSourceFormValues) => {
      createReferralSource({
        name: data.name,
        is_active: data.is_active,
        referred_type: data.referred_type
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          onAdd && onAdd(response.data.id);
          onSuccess();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createReferralSource, dispatch, setCustomMessage, setOpen]
  );

  const flags = useFlags();
  const isExtendedSubReferralSourceEnabled =
    flags[EXTENDED_SUB_REFERRAL_SOURCE_FEATURE];
  const defaultValues: ReferralSourceFormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_VALUES, EMPTY_DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<ReferralSourceFormValues>({
    mode: "onChange",
    defaultValues
  });
  const { control, handleSubmit, reset } = formMethods;

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      onReferralSourceSave(data);
    })();
  }, [handleSubmit, onReferralSourceSave]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <ModalForm
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={onSubmit}
      title="New Referral Source Add"
      hideBackdrop={false}
      formName="physician"
      maxWidth="lg"
      control={control}
    >
      <Grid item container xs={12} spacing={4}>
        <Grid item xs={isExtendedSubReferralSourceEnabled ? 5 : 7}>
          <InputFormField
            name="name"
            label="Referral Source"
            control={control}
            required
          />
        </Grid>
        {isExtendedSubReferralSourceEnabled && (
          <Grid item xs={2}>
            <SelectFormField
              name="referred_type"
              label="Associated Data"
              control={control}
              options={REFERRAL_SOURCE_OBJECT_TYPES.getOptions()}
              sx={{ ml: 1, mt: 1 }}
            />
          </Grid>
        )}
        <Grid item xs={5}>
          <FormControlLabel
            label="Active"
            labelPlacement="start"
            sx={{ ml: 2, mt: 3.5 }}
            control={
              <CheckboxFormField
                testId="is_active"
                name="is_active"
                control={control}
              />
            }
          />
        </Grid>
      </Grid>
    </ModalForm>
  );
}
